import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { BtnPrimary, ShadowBox } from './scaffolds';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

const HowToVoItem: React.FC<{
  style?: React.CSSProperties;
  className?: string;
  currentIndex?: number;
  contents?: HowToVoContent[];
}> = ({ style, className, currentIndex = 0, contents = [] }) => (
  <div style={style} className={className}>
    <div tw="grid grid-cols-4 gap-0 lg:gap-2 relative mb-4 lg:mb-14">
      {contents.map((item, index) => (
        <div key={'howtoitem' + index} tw="col-span-4 lg:col-span-1">
          <div
            tw="text-b1 p-3 rounded-2xl flex flex-col items-center justify-center"
            css={css`
              min-height: 60px;
              ${index <= currentIndex ? tw`bg-blue-marine dark:bg-orange` : tw`bg-blue-sky`}
              ${index <= currentIndex ? tw`text-white dark:text-black-100` : tw`text-blue-marine`}
              @media (max-width: 1023px) {
                ${index === currentIndex ? 'block' : tw`hidden`}
              }
              @media (max-width: 1280px) {
                ${tw`text-b3`}
              }
            `}
          >
            <p tw="mb-0 text-center">{item.summary}</p>
          </div>
        </div>
      ))}
    </div>
    <div
      css={css`
        overflow: hidden;
      `}
    >
      <SwitchTransition mode="out-in">
        <CSSTransition<undefined>
          key={currentIndex}
          classNames="slide"
          addEndListener={(node: HTMLElement, done: () => void) => {
            node.addEventListener('transitionend', done, false);
          }}
        >
          <div
            tw="mx-4 lg:mx-0"
            css={css`
              @media (max-width: 1024px) {
                flex-wrap: wrap;
              }
            `}
          >
            <h3>{contents[currentIndex]?.title}</h3>
            <div tw="text-b1 mb-14">{contents[currentIndex]?.detail}</div>
          </div>
        </CSSTransition>
      </SwitchTransition>
    </div>
  </div>
);

const HowToVoItemProgress: React.FC<{
  style?: React.CSSProperties;
  className?: string;
  currentIndex?: number;
  length?: number;
}> = ({ style, className, currentIndex = 0, length = 0 }) => (
  <div style={style} className={className} tw="grid grid-cols-4 gap-2 relative">
    {[...Array(length)].map((_, index) => (
      <div key={'how-to-progress' + index} tw="relative">
        <div
          className="line"
          tw="absolute"
          css={css`
            top: 13px;
            height: 4px;
            width: 100%;
            z-index: 1;
            left: 50%;
            ${index < currentIndex ? tw`bg-blue-marine dark:bg-orange` : tw`bg-blue-sky`}
            ${index + 1 === length ? tw`hidden` : tw`block`}
          `}
        ></div>
        <div
          className="line"
          tw="absolute"
          css={css`
            top: 13px;
            height: 4px;
            width: 2vw;
            z-index: 1;
            left: 50%;
            ${index <= currentIndex ? tw`bg-blue-marine dark:bg-orange` : tw`bg-blue-sky`}
            ${index + 1 === length ? tw`hidden` : tw`block`}
          `}
        ></div>
        <div tw="flex justify-center">
          <div
            tw="rounded-full text-center p-2 mb-4"
            css={css`
              height: 32px;
              width: 32px;
              z-index: 2;
              ${index <= currentIndex ? tw`bg-blue-marine dark:bg-orange` : tw`bg-blue-sky`}
              ${index <= currentIndex ? tw`text-white dark:text-black-100` : tw`text-blue-marine`}
            `}
          >
            {index + 1}
          </div>
        </div>
      </div>
    ))}
  </div>
);

interface HowToVoProps {
  title?: any;
  subtitle?: any;
  howToLink?: string;
  onHowToVoLinkClicked?: any;
  buttonClass?: string;
}

interface HowToVoState {
  currentIndex?: number;
}

export interface HowToVoContent {
  title?: string;
  summary?: string;
  detail?: any;
  link?: string;
}

const contents: HowToVoContent[] = [
  {
    summary: 'Hubungi Tim Sales Easybiz',
    title: 'Jadwalkan visit, ajukan pertanyaan, ataupun penyewaan.',
    detail: (
      <div>
        Anda bisa hubungi tim Sales Easybiz via Whatsapp atau langsung melakukan visit pada jam
        operasional Virtual Office (Senin - Jumat jam 09:00 - 18:00). Saat ini lokasi Virtual Office
        beralamat di kantor Easybiz.
      </div>
    ),
  },
  {
    summary: 'Lakukan Pembayaran',
    title: 'Lakukan Pembayaran',
    detail: (
      <div>
        Setelah menghubungi tim Sales Easybiz, kami akan melanjutkan proses ke proses pembayaran.
      </div>
    ),
  },
  {
    summary: 'Tanda Tangan dan Dapatkan Dokumen',
    title: 'Tanda Tangan dan Dapatkan Dokumen',
    detail: (
      <div>
        Setelah pembayaran terkonfirmasi, akan dilanjutkan ke proses penandatanganan dan tim kami
        akan mengirimkan dokumen sebagai tanda bukti layanan.
      </div>
    ),
  },
  {
    summary: 'Siap Gunakan Virtual Office',
    title: 'Siap Gunakan Virtual Office',
    detail: (
      <div>
      Kini Virtual Office siap digunakan oleh Anda dan bisnis Anda! Kami akan melayani secara maksimal dengan benefit yang Anda dapatkan.

      </div>
    ),
  },
];

class HowToVo extends React.Component<HowToVoProps, HowToVoState> {
  constructor(props: HowToVoProps) {
    super(props);
    this.state = {
      currentIndex: 0,
    };
  }

  left() {
    const idx = this.state?.currentIndex || 0;
    if (idx < 1) {
      return;
    }
    this.setState({
      currentIndex: idx - 1,
    });
  }

  right() {
    const idx = this.state?.currentIndex || 0;
    if (idx > 3) {
      return;
    }
    this.setState({
      currentIndex: idx + 1,
    });
  }

  render() {
    const totalData = 4;
    return (
      <section tw="bg-black-700 dark:bg-black-200">
        <div tw="container">
          <div tw="lg:text-center mx-8 lg:mx-32 lg:text-center py-12 lg:pb-28">
            <h2 id="proses" tw="text-black-100 dark:text-white mb-4">
              {this.props.title}
            </h2>
            <div tw="text-b1 mb-10 dark:text-white">{this.props.subtitle}</div>
            <ShadowBox hideDashed={true} tw="bg-white px-6 pt-10 pb-6 rounded-3xl block">
              <div tw="absolute left-1 lg:left-4 h-full top-0 flex flex-col justify-center">
                <button
                  className={this.props.buttonClass || 'prev-button'}
                  tw="cursor-pointer bg-yellow-bright rounded-2xl border-0 py-2 px-3 lg:py-1 lg:px-2 focus:outline-none"
                  onClick={() => this.left()}
                  css={css`
                    ${!this.state?.currentIndex || this.state?.currentIndex < 1
                      ? tw`opacity-20 bg-black-700`
                      : ``}
                  `}
                  disabled={!this.state?.currentIndex || this.state?.currentIndex < 1}
                >
                  <span
                    tw="text-black-300 text-h4 lg:text-h2"
                    className="icon icon-chevron-left"
                    aria-label="slide-left"
                  ></span>
                </button>
              </div>
              <div tw="absolute right-1 lg:right-4 h-full top-0 flex flex-col justify-center">
                <button
                  className={this.props.buttonClass || 'next-button'}
                  tw="cursor-pointer bg-yellow-bright rounded-2xl border-0 py-2 px-3 lg:py-1 lg:px-2 focus:outline-none"
                  onClick={() => this.right()}
                  css={css`
                    ${this.state?.currentIndex > totalData - 2 ? tw`opacity-20 bg-black-700` : ``}
                  `}
                  disabled={this.state?.currentIndex > totalData - 2}
                >
                  <span
                    tw="text-black-300 text-h4 lg:text-h2"
                    className="icon icon-chevron-right"
                    aria-label="slide-right"
                  ></span>
                </button>
              </div>
              <div tw="mb-6 lg:mb-4">
                <HowToVoItemProgress
                  length={4}
                  currentIndex={this.state?.currentIndex}
                ></HowToVoItemProgress>
                <HowToVoItem
                  contents={contents}
                  currentIndex={this.state?.currentIndex}
                ></HowToVoItem>
              </div>
              <p tw="text-b1 text-center mb-4">Punya pertanyaan?</p>
              <div tw="flex justify-center">
                <BtnPrimary
                  className="gtm-call-button"
                  href={this.props.howToLink || '/hubungi-kami-via-whatsapp'}
                  target="__blank"
                  onClick={this.props.onHowToVoLinkClicked}
                >
                  WhatsApp Kami Di Sini
                </BtnPrimary>
              </div>
            </ShadowBox>
          </div>
        </div>
      </section>
    );
  }
}

export default HowToVo;
